import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { toApiLanguageCode } from '../../../utils/languageMapper';
import { gdprService, type GdprDocument } from '../../../services/gdprService';
import { setConsentBlocking } from '../../../hooks/consentState';
import { ConsentGate } from './index';

/**
 * Decide si hay que mostrar la puerta de consentimiento y registra la decisión.
 * Montado una vez en la raíz; inerte mientras no haya sesión.
 */
export function ConsentGateManager() {
  const { isAuthenticated, user } = useAuth();
  const { i18n, t } = useTranslation();
  const userId = user?.userId ?? '';
  const langCode = toApiLanguageCode(i18n.language);

  const [pending, setPending] = useState<GdprDocument[]>([]);
  const [updated, setUpdated] = useState<GdprDocument[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mientras no llegue la respuesta del getStatus no se sabe si hay documentos
  // pendientes: se trata como bloqueante para que nada (p. ej. el tutorial)
  // arranque en esa ventana.
  const [checked, setChecked] = useState(false);

  const blocking = [...pending, ...updated];

  // El resto de la app (tutorial, avisos contextuales) consulta este estado
  // para no arrancar nada mientras la puerta legal siga abierta (o sin
  // resolver: "desconocido" también bloquea).
  const isBlocking = !checked || blocking.length > 0;
  useEffect(() => {
    setConsentBlocking(isBlocking);
  }, [isBlocking]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setPending([]);
      setUpdated([]);
      setChecked(false);
      return;
    }

    let cancelled = false;
    gdprService
      .getStatus(langCode)
      .then((status) => {
        if (cancelled) return;
        setPending(status.pending);
        setUpdated(status.updated);
        setChecked(true);
      })
      .catch(() => {
        // Un fallo aquí no puede dejar al usuario fuera de la aplicación.
        if (!cancelled) {
          setPending([]);
          setUpdated([]);
          setChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, langCode]);

  const handleAccept = useCallback(
    async (documents: GdprDocument[]) => {
      setSubmitting(true);
      setError(null);
      try {
        await gdprService.recordConsents(documents, 'accepted');
        setPending([]);
        setUpdated([]);
      } catch {
        setError(t('consent.saveError', 'No hemos podido guardar tu respuesta. Inténtalo de nuevo.'));
      } finally {
        setSubmitting(false);
      }
    },
    [t]
  );

  if (!isAuthenticated || !userId) return null;

  if (blocking.length === 0) return null;

  return (
    <ConsentGate
      documents={blocking}
      isUpdate={pending.length === 0}
      submitting={submitting}
      error={error}
      onAccept={handleAccept}
    />
  );
}

export default ConsentGateManager;
